import { Checkbox, Stack, Text, Title } from "@mantine/core";
import type { ReactNode } from "react";
import { SYSTEM_ROLES } from "./role-catalog";
import type { CustomRole } from "./team-queries";

export interface RoleChoicesProps {
  readonly value: readonly string[];
  readonly onChange: (keys: string[]) => void;
  readonly customRoles: readonly CustomRole[];
  /** Whether the Owner role is offered: to an owner, or when the person already holds it. */
  readonly offerOwner: boolean;
  readonly disabled: boolean;
  readonly description: ReactNode;
  readonly error?: ReactNode;
}

/**
 * The roles a login can hold, as ticks: the church's own roles first, then the built-in ones. Shared by a person's
 * access dialog and the invitation, so both offer the same roles under the same owner rule.
 */
export function RoleChoices({
  value,
  onChange,
  customRoles,
  offerOwner,
  disabled,
  description,
  error,
}: RoleChoicesProps) {
  const systemChoices = SYSTEM_ROLES.filter((r) => r.key !== "tenant_owner" || offerOwner);
  const box = (key: string, label: string, text: string) => (
    <Checkbox key={key} value={key} label={label} description={text} disabled={disabled} />
  );
  return (
    <Checkbox.Group
      label={
        <Title order={3} size="h5" component="span">
          Roles
        </Title>
      }
      description={description}
      value={[...value]}
      onChange={onChange}
      error={error}
    >
      <Stack gap="xs" mt="xs">
        {customRoles.length === 0 ? null : (
          <>
            <Text size="xs" fw={600} c="dimmed">
              Your church's roles
            </Text>
            {customRoles.map((r) =>
              box(
                r.key,
                r.name,
                r.description.length > 0 ? r.description : "Built by your church.",
              ),
            )}
            <Text size="xs" fw={600} c="dimmed" mt="xs">
              Built-in roles
            </Text>
          </>
        )}
        {systemChoices.map((r) => box(r.key, r.name, r.description))}
      </Stack>
    </Checkbox.Group>
  );
}
